import { useEffect, useState, useCallback } from "react";
import { FileText, Plus, Loader2, Download, AlertTriangle, FileWarning, CalendarDays } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { fdate } from "@/lib/format";
import { toast } from "sonner";

const ESTATUS = ["En proceso", "Resuelta", "Improcedente", "Escalada"];

const trimestreActual = () => {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()}-T${q}`;
};

const trimestresRecientes = () => {
  const d = new Date();
  const out = [];
  let y = d.getFullYear();
  let q = Math.floor(d.getMonth() / 3) + 1;
  for (let i = 0; i < 6; i++) {
    out.push(`${y}-T${q}`);
    q--;
    if (q < 1) { q = 4; y--; }
  }
  return out;
};

export function QuejasRedecoTab({ can }) {
  const puedeEditar = can("cobranza", "editar") || can("cobranza", "crear");
  const [quejas, setQuejas] = useState([]);
  const [despachos, setDespachos] = useState([]);
  const [periodo, setPeriodo] = useState(trimestreActual());
  const [reporte, setReporte] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [dlgReporte, setDlgReporte] = useState(false);

  const cargar = useCallback(async () => {
    const [q, d] = await Promise.all([
      api.get("/condusef_redeco").then((r) => r.data).catch(() => []),
      api.get("/despachos-cobranza").then((r) => r.data).catch(() => []),
    ]);
    setQuejas(q);
    setDespachos(d);
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const generarReporte = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/condusef/redeco/reporte-trimestral", { params: { periodo } });
      setReporte(data);
      setDlgReporte(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el reporte");
    } finally { setBusy(false); }
  };

  const descargarExcel = () => {
    // El axios `api` respeta el auth; usamos endpoint blob
    api.get("/condusef/redeco/reporte-trimestral/excel", { params: { periodo }, responseType: "blob" })
      .then((r) => {
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement("a");
        a.href = url; a.download = `REDECO_${periodo}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("No se pudo descargar"));
  };

  const openAlta = () => {
    setForm({
      folio: "", fecha: new Date().toISOString().slice(0, 10),
      despacho_id: "", cliente: "", credito: "", motivo: "", estatus: "En proceso",
      resolucion: "", evidencia: "", contrato_despacho: "", credito_id: "", cliente_id: "",
    });
    setDlgOpen(true);
  };

  const guardar = async () => {
    if (!form.motivo?.trim()) { toast.error("Captura el motivo"); return; }
    setBusy(true);
    try {
      await api.post("/condusef/redeco/queja-v2", form);
      toast.success("Queja REDECO registrada");
      setDlgOpen(false);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar");
    } finally { setBusy(false); }
  };

  const COLS = [
    { key: "folio", label: "Folio" },
    { key: "fecha", label: "Fecha", render: (r) => fdate(r.fecha) },
    { key: "despacho", label: "Despacho", render: (r) => r.despacho || <span className="text-slate-400">Sin vincular</span> },
    { key: "cliente", label: "Cliente" },
    { key: "credito", label: "Crédito" },
    { key: "motivo", label: "Motivo" },
    { key: "estatus", label: "Estatus", kind: "badge" },
    { key: "acuse", label: "Acuse" },
  ];

  return (
    <div className="space-y-4 py-4">
      {/* Cabecera de reporte */}
      <div data-testid="reporte-card" className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px]">
            <p className="text-xs uppercase tracking-wide text-slate-500">Reporte trimestral REDECO</p>
            <p className="mt-1 text-sm text-slate-700">
              Genera el reporte offline con el padrón de despachos externos, quejas y gestiones fuera de horario del trimestre.
            </p>
            <p className="mt-1 text-[11px] text-amber-700">
              <AlertTriangle className="mr-1 inline h-3 w-3" />
              El layout oficial CONDUSEF debe validarse contra los Anexos vigentes antes de enviarlo a la autoridad.
            </p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-xs">Trimestre</Label>
              <Select value={periodo} onValueChange={setPeriodo}>
                <SelectTrigger data-testid="sel-periodo" className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {trimestresRecientes().map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="btn-generar-reporte" onClick={generarReporte} disabled={busy}
              className="bg-primary hover:bg-sidebar-hover">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileText className="mr-1 h-4 w-4" />} Generar
            </Button>
            <Button data-testid="btn-excel-reporte" variant="outline" onClick={descargarExcel}>
              <Download className="mr-1 h-4 w-4" /> Excel
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testid="kpi-quejas-total" label="Quejas REDECO totales" value={quejas.length} icon="FileWarning" accent="red" />
        <StatCard testid="kpi-quejas-abiertas" label="En proceso" value={quejas.filter((q) => q.estatus === "En proceso").length} icon="AlertTriangle" accent="amber" />
        <StatCard testid="kpi-quejas-resueltas" label="Resueltas" value={quejas.filter((q) => q.estatus === "Resuelta").length} icon="ShieldCheck" accent="green" />
        <StatCard testid="kpi-despachos-en-catalogo" label="Despachos en catálogo" value={despachos.length} icon="Building2" accent="primary" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-sm font-semibold text-slate-700">Quejas de deudores contra despachos</h3>
        {puedeEditar && (
          <Button data-testid="btn-nueva-queja" size="sm" onClick={openAlta} className="bg-primary hover:bg-sidebar-hover">
            <Plus className="mr-1 h-4 w-4" /> Nueva queja
          </Button>
        )}
      </div>
      <DataTable testid="tabla-quejas-redeco" columns={COLS} rows={quejas} emptyLabel="Sin quejas REDECO capturadas" />

      {/* Dialog alta queja */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Nueva queja REDECO</DialogTitle>
            <DialogDescription>Queja de un deudor contra un despacho de cobranza. Se enlaza al padrón para el reporte trimestral.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Despacho *</Label>
              <Select value={form.despacho_id || ""} onValueChange={(v) => setForm({ ...form, despacho_id: v })}>
                <SelectTrigger data-testid="q-despacho"><SelectValue placeholder="Selecciona el despacho" /></SelectTrigger>
                <SelectContent>
                  {despachos.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`q-desp-${d.id}`}>
                      {d.razon_social}{d.es_externo ? " · Externo" : " · Interno"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Folio</Label>
              <Input data-testid="q-folio" value={form.folio || ""} onChange={(e) => setForm({ ...form, folio: e.target.value })} placeholder="Auto si vacío" />
            </div>
            <div>
              <Label>Fecha</Label>
              <Input type="date" data-testid="q-fecha" value={form.fecha || ""} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
            </div>
            <div>
              <Label>Cliente</Label>
              <Input data-testid="q-cliente" value={form.cliente || ""} onChange={(e) => setForm({ ...form, cliente: e.target.value })} />
            </div>
            <div>
              <Label>Crédito</Label>
              <Input data-testid="q-credito" value={form.credito || ""} onChange={(e) => setForm({ ...form, credito: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Motivo *</Label>
              <Textarea data-testid="q-motivo" rows={2} value={form.motivo || ""} onChange={(e) => setForm({ ...form, motivo: e.target.value })} />
            </div>
            <div>
              <Label>Estatus</Label>
              <Select value={form.estatus || "En proceso"} onValueChange={(v) => setForm({ ...form, estatus: v })}>
                <SelectTrigger data-testid="q-estatus"><SelectValue /></SelectTrigger>
                <SelectContent>{ESTATUS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Acuse</Label>
              <Input data-testid="q-acuse" value={form.acuse || ""} onChange={(e) => setForm({ ...form, acuse: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Resolución</Label>
              <Textarea data-testid="q-resolucion" rows={2} value={form.resolucion || ""} onChange={(e) => setForm({ ...form, resolucion: e.target.value })} />
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDlgOpen(false)}>Cancelar</Button>
            <Button data-testid="q-guardar" onClick={guardar} disabled={busy} className="bg-primary hover:bg-sidebar-hover">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog reporte trimestral */}
      <Dialog open={dlgReporte} onOpenChange={setDlgReporte}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {reporte && (
            <>
              <DialogHeader>
                <DialogTitle>Reporte REDECO — {reporte.periodo}</DialogTitle>
                <DialogDescription>
                  Del {reporte.trimestre_desde} al {reporte.trimestre_hasta} · Generado por {reporte.generado_por}
                </DialogDescription>
              </DialogHeader>
              <div data-testid="reporte-body" className="space-y-4 text-sm">
                <div data-testid="reporte-advertencia" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900">
                  <AlertTriangle className="mr-1 inline h-4 w-4" /> {reporte.advertencia}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <p className="text-[11px] uppercase text-slate-500">Padrón — externos activos</p>
                    <p data-testid="rep-padron-total" className="mt-1 font-heading text-2xl font-semibold">{reporte.padron.despachos_externos}</p>
                  </div>
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <p className="text-[11px] uppercase text-slate-500">Quejas en el trimestre</p>
                    <p data-testid="rep-quejas-total" className="mt-1 font-heading text-2xl font-semibold">{reporte.quejas.total}</p>
                  </div>
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <p className="text-[11px] uppercase text-slate-500">Gestiones fuera de horario</p>
                    <p data-testid="rep-fuera-total" className="mt-1 font-heading text-2xl font-semibold">{reporte.horario.gestiones_fuera}</p>
                  </div>
                </div>

                <div>
                  <p className="mb-1 text-xs font-semibold uppercase text-slate-500">Padrón</p>
                  <ul className="ml-5 list-disc">
                    {reporte.padron.detalle.map((d) => (
                      <li key={d.id}><b>{d.razon_social}</b> — RFC {d.rfc || "—"} · {d.personas_ejecutoras?.length || 0} persona(s)
                        {d.ultima_confirmacion_padron ? <span className="ml-2 text-emerald-700 text-xs">✓ padrón {d.ultima_confirmacion_padron}</span>
                          : <span className="ml-2 text-amber-700 text-xs">◦ padrón pendiente</span>}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <p className="mb-1 text-xs font-semibold uppercase text-slate-500">Resumen por despacho</p>
                  <ul className="ml-5 list-disc">
                    {reporte.quejas.por_despacho.map((r) => (
                      <li key={r.despacho_id || r.despacho}><b>{r.despacho || "(sin despacho)"}</b> — {r.total} queja(s)</li>
                    ))}
                    {reporte.quejas.por_despacho.length === 0 && <li className="text-slate-400 list-none">Sin quejas en el trimestre</li>}
                  </ul>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button data-testid="btn-descargar-excel-modal" variant="outline" onClick={descargarExcel}>
                  <Download className="mr-1 h-4 w-4" /> Descargar Excel
                </Button>
                <Button onClick={() => setDlgReporte(false)}>Cerrar</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
